import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface SectionMenuEntry {
  readonly id: string;
  readonly path: string;
  readonly label: string;
  /** Drawn at the entry's right edge: the Access Queue's counts. */
  readonly trailing?: ReactNode;
  /** A rule above this entry, between working sections and reference ones. */
  readonly ruleBefore?: boolean;
}

/**
 * THE SECTION MENU (SIR-7424 demo R3-2, shared since SIR-7804): a left sub-nav of a page's
 * sections, the current section on the right. Access and Settings both draw it, so the two
 * pages cannot drift apart. The caller filters `entries` by its own gates first; this file
 * draws what it is given and decides nothing about who may see what.
 */
export function SectionMenu({
  label,
  entries,
  current,
  children,
}: {
  label: string;
  entries: readonly SectionMenuEntry[];
  current: string;
  children: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '200px minmax(0,1fr)',
        height: '100%',
        minHeight: 0,
      }}
    >
      <nav
        aria-label={label}
        style={{
          borderRight: '1px solid var(--border)',
          background: 'var(--surface)',
          padding: '14px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        {entries.map((entry) => {
          const isCurrent = entry.id === current;
          return (
            <div key={entry.id}>
              {entry.ruleBefore === true ? (
                <div
                  aria-hidden="true"
                  style={{ height: 1, background: 'var(--border)', margin: '8px 6px' }}
                />
              ) : null}
              {/* `.li` styles the current entry on `aria-current="true"`, as the list panes
                  do; the rail's `.nav-r` is the one that keys on "page". */}
              <Link
                to={entry.path}
                className="li"
                {...(isCurrent ? { 'aria-current': 'true' as const } : {})}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: 'none',
                  color: 'var(--text)',
                }}
              >
                {entry.label}
                {entry.trailing}
              </Link>
            </div>
          );
        })}
      </nav>
      <div style={{ minWidth: 0, height: '100%', minHeight: 0, overflowY: 'auto' }}>{children}</div>
    </div>
  );
}
